import { useEffect, useRef, useState } from "react";
import {
  useConversations,
  useDeleteConversation,
} from "../../hooks/use-conversations";
import { useAuth } from "../../contexts/AuthContext";
import { Trash } from "lucide-react";
import { Card } from "@/components/ui/card";

interface Props {
  projectId: number | null;
  onSelect: (conversationId: string) => void;
}

export default function ConversationList({ projectId, onSelect }: Props) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useConversations(projectId, 20);
  const { token } = useAuth();
  const deleteMutation = useDeleteConversation();

  // Track whether user has scrolled (prevents auto-fetch on initial render)
  const [userScrolled, setUserScrolled] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Attach scroll listener to detect user scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = () => {
      if (el.scrollTop > 0) setUserScrolled(true);
    };
    el.addEventListener("scroll", handler);
    return () => el.removeEventListener("scroll", handler);
  }, []);

  // Infinite scroll sentinel (activate only after user interacted)
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage || !userScrolled) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, userScrolled]);

  const conversations = data?.pages.flatMap((p) => p.conversations ?? []) ?? [];

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-2 overflow-auto max-h-full p-2"
    >
      {conversations.map((conv) => (
        <Card
          key={conv.id}
          className="flex flex-row items-center justify-between hover:shadow-md transition-shadow cursor-pointer p-4"
        >
          <button
            className="flex-1 text-left"
            onClick={() => onSelect(conv.id)}
          >
            <div className="font-medium line-clamp-1 text-gray-900">
              {conv.title || "Untitled Conversation"}
            </div>
            <div className="text-xs text-gray-500">
              {new Date(conv.last_activity || "").toLocaleString()}
            </div>
          </button>
          <button
            className="ml-2 text-gray-400 hover:text-red-600"
            onClick={(e) => {
              e.stopPropagation();
              deleteMutation.mutate({ conversationId: conv.id, token });
            }}
            disabled={deleteMutation.isPending}
            aria-label="Delete conversation"
          >
            <Trash className="h-4 w-4" />
          </button>
        </Card>
      ))}
      <div ref={sentinelRef} />
    </div>
  );
}
